'use client';

import { ArrowDown, Loader2, Minus, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import type { CartItem } from '@/features/totems/domain/model/cart-item';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { getCartLineInfo } from '@/features/totems/domain/service/cart-line';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { AvailableDiscountsHint } from '@/features/totems/presentation/components/web-totem/layout/available-discounts-hint';
import { CartTotalsBreakdown } from '@/features/totems/presentation/components/web-totem/layout/cart-totals-breakdown';
import { useDiscountCode } from '@/features/totems/presentation/hooks/web-totem/use-discount-code';
import {
  useTotemActions,
  useTotemCartState,
  useTotemCatalog,
  useTotemOrder,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { cn } from '@/shared/utils/utils';

interface TotemCartItemRowProps {
  item: CartItem;
  menuProducts?: MenuSnapshotProduct[];
  onEditOptions?: (product: MenuSnapshotProduct, itemId: string) => void;
  onUpdateQuantity: (id: string, qty: number) => void;
  onRemoveItem: (id: string) => void;
}

function TotemCartItemRow({
  item,
  menuProducts,
  onEditOptions,
  onUpdateQuantity,
  onRemoveItem,
}: TotemCartItemRowProps) {
  const { lineTotal, product, isAtStockLimit, hasOptions } = getCartLineInfo(item, [item], menuProducts);

  return (
    <li className="flex flex-col gap-2 border-b border-border py-3 last:border-b-0 dark:border-zinc-800">
      {/* Top row: Avatar + Name & Price */}
      <div className="flex items-start gap-2.5">
        <Avatar className="size-10 shrink-0 rounded-lg border border-border bg-muted dark:border-zinc-700 dark:bg-zinc-800">
          <AvatarImage alt={item.name} src={item.imageUrl} className="object-cover" />
          <AvatarFallback className="rounded-lg bg-muted font-bold text-foreground dark:bg-zinc-800 dark:text-white">
            {item.name.slice(0, 1)}
          </AvatarFallback>
        </Avatar>

        <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
          <div className="flex flex-col">
            <span className="line-clamp-2 text-sm leading-snug font-bold text-foreground dark:text-white">
              {item.name}
            </span>
          </div>
          <div className="flex shrink-0 flex-col items-end">
            {item.originalPrice && item.originalPrice > item.unitPrice && (
              <span className="text-xs text-muted-foreground line-through">
                {formatTotemPrice(item.originalPrice * item.quantity)}
              </span>
            )}
            <span className="text-sm font-bold text-orange-500">{formatTotemPrice(lineTotal)}</span>
          </div>
        </div>
      </div>

      {/* Subselections / Modifiers list */}
      {item.subselections.length > 0 && (
        <div className="ml-12.5 flex flex-col gap-0.5 border-l-2 border-emerald-500/20 pl-2">
          {item.subselections.map(sub => (
            <span
              key={sub.subselectionItemId}
              className="text-xs leading-tight font-medium text-emerald-600 dark:text-emerald-400"
            >
              • {sub.quantity > 1 ? `${sub.quantity}x ` : ''}
              {sub.name} {sub.price > 0 ? `(+${formatTotemPrice(sub.price)})` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Bottom row: Secondary actions (left) and Stepper (right) */}
      <div className="ml-12.5 flex items-center justify-between pt-0.5">
        {/* Left: Edit (if has options) & Delete buttons */}
        <div className="flex items-center gap-1">
          {hasOptions && product && onEditOptions && (
            <button
              type="button"
              className="flex h-6.5 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground dark:hover:bg-zinc-800"
              onClick={() => onEditOptions(product, item.id)}
            >
              <Pencil className="size-3" />
              <span>Editar</span>
            </button>
          )}

          <button
            type="button"
            className="flex h-6.5 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive dark:hover:bg-red-950/40"
            onClick={() => onRemoveItem(item.id)}
            title="Eliminar producto"
          >
            <Trash2 className="size-3" />
            <span>Eliminar</span>
          </button>
        </div>

        {/* Right: Clean, neutral stepper */}
        <div className="flex items-center rounded-lg border border-border bg-muted/30 p-0.5 dark:border-zinc-700 dark:bg-zinc-800/60">
          <button
            type="button"
            className="flex size-6.5 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-background hover:text-foreground dark:text-white dark:hover:bg-zinc-700"
            onClick={() => {
              if (item.quantity > 1) {
                onUpdateQuantity(item.id, item.quantity - 1);
              } else {
                onRemoveItem(item.id);
              }
            }}
            aria-label="Disminuir cantidad"
          >
            <Minus className="size-3 stroke-[2.5]" />
          </button>

          <span className="w-7 text-center text-xs font-bold text-foreground dark:text-white">{item.quantity}</span>

          <button
            type="button"
            disabled={isAtStockLimit}
            className="flex size-6.5 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 dark:text-white dark:hover:bg-zinc-700"
            onClick={() => {
              if (!isAtStockLimit) {
                onUpdateQuantity(item.id, item.quantity + 1);
              }
            }}
            aria-label="Aumentar cantidad"
          >
            <Plus className="size-3 stroke-[2.5]" />
          </button>
        </div>
      </div>
    </li>
  );
}

export function TotemOrderPanel() {
  return (
    <>
      {/* Desktop view */}
      <div className="hidden h-full lg:flex">
        <DesktopOrderPanelContent />
      </div>
      {/* Mobile view (Drawer) */}
      <div className="fixed bottom-0 left-0 z-50 flex w-full shadow-[0_-10px_30px_rgba(0,0,0,0.15)] lg:hidden">
        <MobileOrderDrawer />
      </div>
    </>
  );
}

function DesktopOrderPanelContent() {
  const { totem, menu } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const { orderType } = useTotemOrder();
  const {
    setCheckoutOpen,
    setSelectedProduct,
    setEditingCartItemId,
    desktopCheckoutRef,
    setOrderOptionsOpen,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
  } = useTotemActions();

  const {
    discountCode,
    discountError,
    isPending,
    changeDiscountCode,
    applyDiscount: handleApplyDiscount,
  } = useDiscountCode({ locationId: totem?.location_id, cart });

  return (
    <aside className="z-10 flex h-full w-87.5 shrink-0 flex-col border-l border-border bg-background shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.03)] dark:border-zinc-800 dark:bg-[#2C2C2E]">
      {/* Header */}
      <div
        ref={desktopCheckoutRef as React.RefObject<HTMLDivElement>}
        className="flex shrink-0 flex-col gap-3.5 border-b border-border px-4 py-4 dark:border-zinc-800"
      >
        <div className="flex w-full items-center rounded-xl bg-muted/40 p-1 dark:bg-zinc-800/40">
          <button
            type="button"
            onClick={() => {
              setOrderType('takeaway');
              setPickupMethod(null);
              setBeeperNumber('');
            }}
            className={cn(
              'flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
              orderType === 'takeaway' || !orderType
                ? 'bg-zinc-200 text-foreground shadow-sm dark:bg-[#4a4a4c]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Para llevar
          </button>
          <button
            type="button"
            onClick={() => {
              setOrderType('eat_here');
              setOrderOptionsOpen(true);
            }}
            className={cn(
              'flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
              orderType === 'eat_here'
                ? 'bg-zinc-200 text-foreground shadow-sm dark:bg-[#4a4a4c]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Comer aquí
          </button>
        </div>

        <div className="flex w-full items-center justify-between">
          <h2 className="text-xl leading-none font-bold tracking-tight text-foreground dark:text-white">Tu pedido</h2>
          <div className="flex flex-col items-end">
            <span className="text-base leading-none font-bold text-foreground dark:text-white">{cart.itemCount}</span>
            <span className="text-xs font-medium text-muted-foreground dark:text-zinc-400">productos</span>
          </div>
        </div>
      </div>

      {/* Discount Code Section */}
      <div className="border-b border-border bg-muted/20 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-800/20">
        <div className="flex w-full items-center gap-2 rounded-xl border border-border bg-background p-1 dark:border-zinc-700 dark:bg-[#3a3a3a]">
          <Input
            placeholder="Código de descuento"
            className="h-8 border-none bg-transparent px-2.5 text-sm font-semibold text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 dark:text-white dark:placeholder:text-zinc-500"
            value={discountCode}
            onChange={e => {
              changeDiscountCode(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleApplyDiscount();
            }}
            disabled={isPending || cart.discount !== null}
          />
          {cart.discount ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs font-bold text-red-400 hover:bg-transparent hover:text-red-300"
              onClick={() => cart.clearDiscount()}
            >
              Quitar
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-8 rounded-lg bg-[#dce3fe] px-3 text-xs font-bold text-[#2c2c2e] hover:bg-[#c9d3fd]"
              onClick={handleApplyDiscount}
              disabled={isPending || !discountCode.trim()}
            >
              {isPending ? <Loader2 className="size-3.5 animate-spin" /> : 'Aplicar'}
            </Button>
          )}
        </div>
        {discountError && <p className="mt-1.5 px-2 text-xs font-medium text-red-400">{discountError}</p>}
        <AvailableDiscountsHint items={cart.items} hidden={cart.discount !== null} />
        {cart.discount && (
          <p className="mt-1.5 px-2 text-xs font-medium text-emerald-500 dark:text-emerald-400">
            Descuento aplicado: {cart.discount.code}
            {cart.discount.type === 'percentage'
              ? ` (${cart.discount.amount}%)`
              : ` (-${formatTotemPrice(cart.discount.amount)})`}
          </p>
        )}
      </div>

      {/* Content - Items List */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-1">
        {cart.items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center text-muted-foreground dark:text-zinc-500">
            <ShoppingCartIcon className="size-16 opacity-30" />
            <p className="text-lg font-medium">Tu pedido está vacío</p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {cart.items.map(item => (
              <TotemCartItemRow
                key={item.id}
                item={item}
                menuProducts={menu?.products}
                onEditOptions={(product, itemId) => {
                  setEditingCartItemId(itemId);
                  setSelectedProduct(product);
                }}
                onUpdateQuantity={(id, qty) => cart.updateQuantity(id, qty)}
                onRemoveItem={id => cart.removeItem(id)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex shrink-0 flex-col gap-3.5 border-t border-border bg-background px-4 py-4 dark:border-zinc-800 dark:bg-[#2C2C2E]">
        <CartTotalsBreakdown items={cart.items} discount={cart.discount} />

        <div className="flex items-center justify-between text-foreground dark:text-white">
          <span className="text-lg font-bold">Total</span>
          <div className="flex items-start gap-1 text-orange-500">
            <span className="mt-0.5 text-base font-bold">$</span>
            <span className="text-2xl font-black tracking-tight">
              {new Intl.NumberFormat('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(Math.trunc(cart.total * 100) / 100)}
            </span>
          </div>
        </div>

        <div className="flex h-12 gap-2.5">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="size-12 shrink-0 rounded-xl border-red-200 bg-red-50 p-0 text-orange-500 shadow-none hover:border-red-300 hover:bg-red-100 hover:text-orange-600 dark:border-red-500/20 dark:bg-red-500/10 dark:hover:border-red-500/30 dark:hover:bg-red-500/20"
                disabled={cart.items.length === 0}
              >
                <Trash2 className="size-5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Vaciar el pedido?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminarán todos los productos de tu pedido actual. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Volver</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    cart.clearCart();
                    setOrderType(null);
                    setPickupMethod(null);
                    setBeeperNumber('');
                  }}
                >
                  Vaciar pedido
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            className="h-full flex-1 rounded-xl bg-[#3ba859] text-lg font-bold text-white shadow-md transition-all hover:bg-[#34964e] hover:shadow-lg active:scale-[0.98]"
            disabled={cart.items.length === 0}
            onClick={() => setTimeout(() => setCheckoutOpen(true), 150)}
          >
            Cobrar
          </Button>
        </div>
      </div>
    </aside>
  );
}

function ShoppingCartIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
      />
    </svg>
  );
}

function MobileOrderDrawer() {
  const { totem, menu } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const {
    setCheckoutOpen,
    setSelectedProduct,
    setEditingCartItemId,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
    mobileCheckoutRef,
  } = useTotemActions();

  const {
    discountCode,
    discountError,
    isPending,
    changeDiscountCode,
    applyDiscount: handleApplyDiscount,
  } = useDiscountCode({ locationId: totem?.location_id, cart });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 640px)');
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches && open) {
        setOpen(false);
      }
    };
    handleResize(mediaQuery);
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, [open]);

  const truncatedTotal = Math.trunc(cart.total * 100) / 100;
  const hasDecimals = truncatedTotal % 1 !== 0;
  const formattedTotal = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(truncatedTotal);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          ref={mobileCheckoutRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="relative flex w-full flex-col items-end justify-end bg-[#353535] px-4 pt-6 pb-3 transition-transform active:scale-[0.98]"
        >
          {/* Fake rounded corners: sits inside the black bar so the black background is visible behind the curves */}
          <div className="absolute top-0 right-0 left-0 h-6 rounded-b-4xl bg-[#e5e5e5]" />

          <div className="relative z-10 flex items-start gap-3 pt-2">
            <div className="flex flex-col items-start">
              <div className="flex h-11 min-w-22.5 items-center justify-start rounded-lg bg-[#c8d4fc] px-4">
                <span className="text-xl leading-none font-black text-[#2c2c2e]">${formattedTotal}</span>
              </div>
              <span className="mt-1 pl-4 text-[11px] font-bold text-white">Total</span>
            </div>

            <div className="flex h-11 items-center justify-center rounded-lg bg-[#4fd16d] px-6 text-base font-bold text-black shadow-sm">
              Pagar
            </div>
          </div>
        </button>
      </DrawerTrigger>

      <DrawerContent className="mt-0! flex h-dvh max-h-none! flex-col border-none bg-transparent p-0 text-white [&>div.mx-auto]:hidden">
        <DrawerTitle className="sr-only">Tu pedido</DrawerTitle>
        <div className="relative z-20 flex shrink-0 justify-end rounded-b-3xl bg-[#e5e5e5] px-4 pt-2 pb-2">
          <DrawerClose asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-[#c8d4fc] px-4 py-2 font-bold text-black transition-opacity hover:opacity-90"
            >
              <span className="text-[15px]">Volver al inicio</span>
              <ArrowDown className="size-5" />
            </button>
          </DrawerClose>
        </div>

        <div className="relative z-10 -mt-6 flex min-h-0 flex-1 flex-col bg-[#353535] px-4">
          <div className="shrink-0 pt-8 pb-4">
            <label htmlFor="discount-code-mobile" className="mb-2 block text-sm font-medium text-[#e0e0e0]">
              Ingresá tu código de descuento
            </label>
            <div className="flex w-full items-center gap-2 rounded-xl border border-zinc-600 bg-[#2c2c2e] p-1.5">
              <Input
                id="discount-code-mobile"
                placeholder="CÓDIGO DE DESCUENTO"
                className="h-9 border-none bg-transparent px-3 text-sm font-semibold text-white uppercase shadow-none placeholder:text-zinc-500 focus-visible:ring-0"
                value={discountCode}
                onChange={e => {
                  changeDiscountCode(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleApplyDiscount();
                }}
                disabled={isPending || cart.discount !== null}
              />
              {cart.discount ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-4 font-bold text-red-400 hover:bg-transparent hover:text-red-300"
                  onClick={() => cart.clearDiscount()}
                >
                  Quitar
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-9 rounded-lg bg-[#c8d4fc] px-4 font-bold text-[#2c2c2e] hover:bg-[#b0c0f9]"
                  onClick={handleApplyDiscount}
                  disabled={isPending || !discountCode.trim()}
                >
                  {isPending ? <Loader2 className="size-4 animate-spin" /> : 'Aplicar'}
                </Button>
              )}
            </div>
            {discountError && <p className="mt-2 px-2 text-xs font-medium text-red-400">{discountError}</p>}
            <AvailableDiscountsHint items={cart.items} hidden={cart.discount !== null} />
            {cart.discount && (
              <p className="mt-2 px-2 text-xs font-medium text-emerald-400">
                Descuento aplicado: {cart.discount.code}
                {cart.discount.type === 'percentage'
                  ? ` (${cart.discount.amount}%)`
                  : ` (-${formatTotemPrice(cart.discount.amount)})`}
              </p>
            )}
          </div>

          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-bold">Tu pedido</h2>
            <span className="text-sm text-zinc-400">
              {cart.itemCount} producto{cart.itemCount !== 1 && 's'}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto pb-3">
            {cart.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 py-12 text-center text-zinc-500">
                <ShoppingCartIcon className="size-16 opacity-30" />
                <p className="text-lg font-medium">Tu pedido está vacío</p>
              </div>
            ) : (
              <ul className="flex flex-col">
                {cart.items.map(item => (
                  <TotemCartItemRow
                    key={item.id}
                    item={item}
                    menuProducts={menu?.products}
                    onEditOptions={(product, itemId) => {
                      setEditingCartItemId(itemId);
                      setSelectedProduct(product);
                      setOpen(false);
                    }}
                    onUpdateQuantity={(id, qty) => cart.updateQuantity(id, qty)}
                    onRemoveItem={id => cart.removeItem(id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="shrink-0 bg-[#353535] px-4 py-4 pb-6">
          <CartTotalsBreakdown items={cart.items} discount={cart.discount} dark className="mb-3" />

          <div className="mb-4 flex items-center gap-3">
            <span className="text-xl font-bold text-white">Total</span>
            <div className="h-px flex-1 bg-zinc-600" />
            <span className="text-2xl font-black text-white">${formattedTotal}</span>
          </div>

          <div className="flex h-12 gap-3">
            <Button
              className="h-full flex-1 rounded-xl bg-[#ff6b6b] text-base font-bold text-white hover:bg-[#ff5252]"
              disabled={cart.items.length === 0}
              onClick={() => {
                cart.clearCart();
                setOrderType(null);
                setPickupMethod(null);
                setBeeperNumber('');
                setOpen(false);
              }}
            >
              <Trash2 className="mr-2 size-4.5" /> Cancelar
            </Button>
            <Button
              className="h-full flex-1 rounded-xl bg-[#4fd16d] text-base font-bold text-white hover:bg-[#45b960]"
              disabled={cart.items.length === 0}
              onClick={() => {
                setOpen(false);
                setTimeout(() => setCheckoutOpen(true), 150);
              }}
            >
              Pagar
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
